import { useLang } from '../context/LanguageContext';
import LegalPageShell from '../components/LegalPageShell';
import { BUSINESS, businessPhoneDisplay } from '../config/business';
import { usePageMeta } from '../hooks/usePageMeta';
import { LEGAL_TE_SUMMARIES } from '../content/legalContent';

export function TermsContent() {
  return (
    <>
      <p className="legal-updated">Last updated: September 2026</p>

      <p className="legal-highlight">
        Repatikosam is operated by <strong>{BUSINESS.legalName}</strong> (GSTIN {BUSINESS.gstin}). We serve readers
        in India and internationally. Payments are in INR via Razorpay. See our{' '}
        <a href="/refund">Refund</a> and <a href="/cancellation">Cancellation</a> policies.
      </p>

      <div className="legal-section">
        <h3>1. Acceptance of Terms</h3>
        <p>
          By accessing and using the Repatikosam website, iOS app, and related digital services, you accept and
          agree to be bound by these Terms and Conditions. If you do not agree, please do not use our services.
        </p>
      </div>

      <div className="legal-section">
        <h3>2. About Repatikosam &amp; Registered Business</h3>
        <p>
          Repatikosam (రేపటికోసం) is a Telugu daily and monthly digital newspaper for the Telugu community,
          operated by <strong>{BUSINESS.legalName}</strong>. We provide news, articles, and digital e-paper
          through the website and the Repatikosam iOS app.
        </p>
      </div>

      <div className="legal-section">
        <h3>3. Subscription Services</h3>
        <p>
          Access to our full digital e-paper requires a paid annual subscription. Fees are displayed at checkout
          (currently ₹999 + 18% GST). Payment is processed securely via Razorpay. By subscribing, you agree to pay
          the applicable fees shown at the time of purchase.
        </p>
      </div>

      <div className="legal-section">
        <h3>4. International Customers &amp; Payments</h3>
        <p>
          Repatikosam welcomes subscribers in India and internationally. All prices are quoted in{' '}
          <strong>Indian Rupees (INR)</strong>. Payments are processed by <strong>Razorpay</strong>, which supports
          domestic and international credit/debit cards where enabled by your bank and Razorpay.
        </p>
        <ul>
          <li>Your card issuer or bank may charge currency conversion or international transaction fees</li>
          <li>The final amount charged may differ slightly from the INR price shown due to exchange rates</li>
          <li>Digital access is delivered online — no physical goods are shipped</li>
          <li>Indian GST (where applicable) is shown at checkout before you pay</li>
        </ul>
      </div>

      <div className="legal-section">
        <h3>5. User Responsibilities</h3>
        <p>You agree to:</p>
        <ul>
          <li>Provide accurate information during registration (Google sign-in)</li>
          <li>Not share your subscription access with others</li>
          <li>Not reproduce or distribute our content without permission</li>
          <li>Use our services only for lawful purposes</li>
        </ul>
      </div>

      <div className="legal-section">
        <h3>6. Intellectual Property</h3>
        <p>
          All content published on Repatikosam — including articles, images, and the e-paper — is the intellectual
          property of Repatikosam. Unauthorized reproduction is strictly prohibited.
        </p>
      </div>

      <div className="legal-section">
        <h3>7. Limitation of Liability</h3>
        <p>
          Repatikosam shall not be liable for any indirect, incidental, or consequential damages arising from the
          use of our services. We strive for accuracy but do not guarantee the completeness of all information
          published.
        </p>
      </div>

      <div className="legal-section">
        <h3>8. Related Policies</h3>
        <p>
          Please also read our <a href="/privacy">Privacy Policy</a>, <a href="/refund">Refund Policy</a>, and{' '}
          <a href="/cancellation">Cancellation Policy</a>.
        </p>
      </div>

      <div className="legal-section">
        <h3>9. Modifications</h3>
        <p>
          We reserve the right to modify these terms at any time. Continued use of our services after changes
          constitutes acceptance of the new terms.
        </p>
      </div>

      <div className="legal-section">
        <h3>10. Governing Law</h3>
        <p>
          These terms are governed by the laws of India. Any disputes shall be subject to the jurisdiction of
          courts in Andhra Pradesh, India. International customers agree that mandatory consumer protections of
          their home country may apply where required by law.
        </p>
      </div>

      <div className="legal-section">
        <h3>11. Contact</h3>
        <p>
          For any questions regarding these terms: <strong>{BUSINESS.email}</strong> ·{' '}
          <strong>{businessPhoneDisplay()}</strong>
        </p>
      </div>

      <div className="legal-section">
        <h3>12. iOS App</h3>
        <p>
          These Terms also apply to the Repatikosam iOS application. Using the app on your iPhone or iPad means you
          accept these Terms. The app and the website at <a href={BUSINESS.website}>repatikosam.com</a> are the same
          digital newspaper service operated by <strong>{BUSINESS.legalName}</strong>.
        </p>
        <p>
          Support for the iOS app is provided by M S Publications (not Apple). Contact us at{' '}
          <strong>{BUSINESS.email}</strong> or <strong>{businessPhoneDisplay()}</strong>, or use Menu → Contact /
          WhatsApp in the app. Privacy details are in our in-app Privacy Policy.
        </p>
      </div>

      <div className="legal-section">
        <h3>13. License to use the app</h3>
        <p>
          We grant you a personal, limited, non-exclusive, non-transferable license to install and use the
          Repatikosam app on Apple devices that you own or control, as allowed by the Apple Media Services Terms
          and the Licensed Application End User License Agreement. You may not copy, reverse engineer, rent, or
          sublicense the app. Apple has no obligation to provide maintenance or support for the app.
        </p>
      </div>

      <div className="legal-section">
        <h3>14. Digital subscriptions (Apple App Store)</h3>
        <p>
          Full e-paper access is sold as an <strong>annual digital newspaper pass</strong> (a reader-style
          publication). The price shown at checkout is currently <strong>₹999 + 18% GST</strong> (about ₹1,179) for
          12 months. Payment is processed in the app by Razorpay — Apple does not collect this fee and is not
          responsible for the pass. The pass is a one-time annual purchase and <strong>does not auto-renew</strong>{' '}
          through the App Store. Access lasts until the expiry date shown in Menu.
        </p>
        <ul>
          <li>You can review price, GST, and duration on the subscribe screen before you pay</li>
          <li>Refunds and cancellations follow our in-app Refund and Cancellation policies</li>
          <li>Apple is not a party to your subscription contract with M S Publications</li>
          <li>If you believe the app does not conform to a warranty, you may notify Apple; Apple’s refund
            obligation for the app binary (if any) is limited as described in Apple’s standard EULA</li>
        </ul>
      </div>

      <div className="legal-section">
        <h3>15. Account deletion</h3>
        <p>
          You may request deletion of your Repatikosam account and associated personal data at any time by emailing{' '}
          <strong>{BUSINESS.email}</strong> from your registered Google address, or via Contact / WhatsApp in the app.
          We will delete or anonymize account data except records we must keep for tax, payment, or legal reasons.
        </p>
      </div>

      <div className="legal-section">
        <h3>16. Sign-in, videos, and third parties</h3>
        <p>
          Sign-in uses Google. Videos may play from YouTube. Those services have their own terms and privacy
          policies. We do not host user-generated comments inside the app. The service is intended for a general
          audience and is not directed at children under 13.
        </p>
      </div>
    </>
  );
}

export default function TermsPage({ onClose, standalone = false }) {
  const { t } = useLang();
  usePageMeta({
    title: `${t.footerTerms} | ${t.brandName}`,
    description: `Terms and conditions for Repatikosam e-paper by M S Publications. GSTIN ${BUSINESS.gstin}.`,
    path: '/terms',
    enabled: standalone,
  });
  return (
    <LegalPageShell
      title={t.footerTerms}
      onClose={onClose}
      standalone={standalone}
      teSummary={LEGAL_TE_SUMMARIES.terms}
    >
      <TermsContent />
    </LegalPageShell>
  );
}
